import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Plus, Building2, Loader2, PowerOff } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShippersPage() {
  const { token } = useAuth();
  const [shippers, setShippers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newShipper, setNewShipper] = useState({
    name: '',
    company_id: '',
    email: '',
    phone: '',
    is_active: true,
  });

  const fetchShippers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/shippers`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setShippers(await response.json());
      }
    } catch (error) {
      console.error('Error fetching shippers:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchShippers();
  }, [fetchShippers]);

  const handleCreateShipper = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/shippers`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newShipper),
      });
      if (response.ok) {
        toast.success('Shipper created successfully');
        setDialogOpen(false);
        setNewShipper({ name: '', company_id: '', email: '', phone: '', is_active: true });
        fetchShippers();
      } else {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to create shipper');
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (shipperId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/admin/shippers/${shipperId}/toggle-active`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Shipper ${data.is_active ? 'activated' : 'deactivated'}`);
        fetchShippers();
      } else {
        throw new Error('Failed to toggle shipper status');
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const field = (label, key, opts = {}) => (
    <div>
      <Label className="text-sm text-muted-foreground">{label}</Label>
      <Input
        type={opts.type || 'text'}
        value={newShipper[key]}
        onChange={(e) => setNewShipper((prev) => ({ ...prev, [key]: e.target.value }))}
        placeholder={opts.placeholder}
        className="bg-transparent border-border font-mono text-sm"
        required={opts.required}
      />
    </div>
  );

  return (
      <div className="space-y-6" data-testid="admin-shippers">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-2xl font-semibold">Shippers</h1>
            <p className="text-muted-foreground">Manage your shipper accounts</p>
          </div>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-accent text-accent-foreground" data-testid="add-shipper-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Shipper
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle className="font-outfit">Add New Shipper</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateShipper} className="space-y-4" data-testid="add-shipper-form">
                {field('Name', 'name', { required: true })}
                {field('Company ID (Central Dispatch Account ID)', 'company_id', {
                  placeholder: 'e.g. 6b7d8f88',
                  required: true,
                })}
                {field('Email', 'email', { type: 'email', required: true })}
                {field('Phone', 'phone', { required: true })}

                <div className="flex items-center gap-3">
                  <Switch
                    id="shipper_active"
                    checked={newShipper.is_active}
                    onCheckedChange={(v) => setNewShipper((prev) => ({ ...prev, is_active: v }))}
                  />
                  <Label htmlFor="shipper_active" className="cursor-pointer">
                    Active
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-accent text-accent-foreground"
                  disabled={submitting}
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create Shipper'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading...</div>
        ) : shippers.length === 0 ? (
          <Card className="bg-card border-white/5">
            <CardContent className="py-20 text-center">
              <Building2 className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">No shippers yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                Add your first shipper to start managing accounts
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="rounded-lg border border-white/5 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-card border-b border-white/5">
                <tr>
                  {['Name', 'Company ID', 'Email', 'Phone', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="text-left px-4 py-3 text-muted-foreground font-medium">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shippers.map((shipper) => (
                  <tr
                    key={shipper.id}
                    className="border-b border-white/5 hover:bg-white/2 transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">{shipper.name}</td>
                    <td className="px-4 py-3 font-mono text-xs">{shipper.company_id}</td>
                    <td className="px-4 py-3 text-muted-foreground">{shipper.email}</td>
                    <td className="px-4 py-3 text-muted-foreground">{shipper.phone}</td>
                    <td className="px-4 py-3">
                      <Badge
                        className={
                          shipper.is_active !== false
                            ? 'bg-green-500/10 text-green-500'
                            : 'bg-gray-500/10 text-gray-500'
                        }
                      >
                        {shipper.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleToggleActive(shipper.id)}
                        data-testid={`toggle-shipper-${shipper.id}`}
                      >
                        <PowerOff className="h-4 w-4 mr-1" />
                        {shipper.is_active !== false ? 'Deactivate' : 'Activate'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
